// src/pages/projects/ProjectDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FileText, DollarSign, Briefcase, Calendar, File, Eye,
  Download, Loader2, AlertCircle, ArrowLeft, Clock, Mail, Edit
} from 'lucide-react';
import { formatCurrency, formatBytes } from '@/lib/app-utils';
import { useNotifications } from '@/contexts/NotificationContext';
import { useFileViewer } from '@/contexts/FileViewerContext';
import { getFileFromIDB } from '@/lib/idb-storage';
import { projectsAPI } from '@/services/projects';
import { quotationsAPI } from '@/services/quotations';
import { cn } from '@/lib/utils';
import { timelineAPI } from '@/services/timeline';
import SiteVisitBar from '@/components/projects/SiteVisitBar';

const ProjectDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [latestQuotation, setLatestQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addNotification } = useNotifications();
  const { openViewer } = useFileViewer();
  const [loadingFileId, setLoadingFileId] = useState(null);

  useEffect(() => {
    if (id) {
      fetchProjectDetails();
    }
  }, [id]);

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);

      // Fetch project details
      const projectResponse = await projectsAPI.details(id);
      if (projectResponse.success) {
        const projectData = projectResponse.data;

        // دمج attachments و email_attachments
        const generalAttachments = projectData.attachments || [];
        const emailAttachments = projectData.email_attachments || [];

        const allAttachments = [
          ...generalAttachments.map(f => ({ ...f, category: f.category || 'general' })),
          ...emailAttachments.map(f => ({ ...f, category: 'emails' }))
        ];

        const mappedProject = {
          ...projectData,
          id: projectData.id,
          projectName: projectData.project_name || projectData.projectName,
          companyName: projectData.company_name || projectData.companyName,
          companyAddress: projectData.company_address || projectData.companyAddress,
          contactEmail: projectData.contact_email || projectData.contactEmail,
          contactPhone: projectData.contact_phone || projectData.contactPhone,
          projectLocation: projectData.project_location || projectData.projectLocation,
          workType: projectData.work_type || projectData.workType,
          receivedBy: projectData.received_by || projectData.receivedBy,
          description: projectData.description,
          status: projectData.status,
          projectStatus: projectData.project_status || projectData.projectStatus,
          createdAt: projectData.created_at || projectData.createdAt,
          updatedAt: projectData.updated_at || projectData.updatedAt,
          checklist: projectData.checklist || [],
          attachments: allAttachments,
          timeline: []
        };

        setProject(mappedProject);
      } else {
        throw new Error('Project not found');
      }

      // Timeline
      try {
        const timelineResponse = await timelineAPI.list(id);
        if (timelineResponse.success && timelineResponse.data) {
          const mappedTimeline = timelineResponse.data.map(event => ({
            id: event.id,
            title: event.title,
            description: event.description,
            date: event.date,
            user: event.user_name || 'System',
            eventType: event.event_type || 'general'
          }));

          setProject(prev => ({
            ...prev,
            timeline: mappedTimeline
          }));
        }
      } catch (err) {
        console.warn('Failed to load timeline:', err);
      }

      // Latest quotation
      try {
        const quotationsResponse = await quotationsAPI.list(id);
        if (quotationsResponse.success && quotationsResponse.data && quotationsResponse.data.length > 0) {
          const sorted = quotationsResponse.data.sort((a, b) =>
            new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt)
          );

          const mappedQuotation = {
            ...sorted[0],
            quotationNumber: sorted[0].quotation_number || sorted[0].quotationNumber,
            unitType: sorted[0].unit_type || sorted[0].unitType,
            scrapValue: sorted[0].scrap_value || sorted[0].scrapValue,
            netValue: sorted[0].net_value || sorted[0].netValue,
            versionType: sorted[0].version_type || sorted[0].versionType,
            pdfFileName: sorted[0].pdf_file_name || sorted[0].pdfFileName,
            fileSize: sorted[0].file_size || sorted[0].fileSize,
            fileId: sorted[0].file_id || sorted[0].fileId,
            pdfFile: sorted[0].pdfFile,
            createdAt: sorted[0].created_at || sorted[0].createdAt
          };

          setLatestQuotation(mappedQuotation);
        }
      } catch (err) {
        console.warn('Failed to load quotations:', err);
      }
    } catch (error) {
      console.error('Failed to load project:', error);
      addNotification('Error', error.message || 'Failed to load project details', 'error');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProject = async (updatedProject) => {
    try {
      await projectsAPI.update(id, updatedProject);
      setProject(updatedProject);
    } catch (error) {
      console.error('Failed to update project:', error);
      addNotification('Error', 'Failed to update project', 'error');
    }
  };

  const toggleChecklist = (itemId) => {
    const updatedChecklist = project.checklist.map(item =>
      item.id === itemId ? { ...item, completed: !item.completed } : item
    );
    const updatedProject = { ...project, checklist: updatedChecklist };
    handleUpdateProject(updatedProject);
  };

  const getFileUrl = async (file) => {
    // 1. Company files
    if (file.file_id && file.file_id.startsWith('file_')) {
      const downloadUrl = `/api/company-files/download.php?id=${file.file_id}`;
      const fullUrl = `${import.meta.env.VITE_API_URL || 'https://new.sbdemolition.online'}${downloadUrl}`;
      return { url: fullUrl, blob: null, isServerFile: true };
    }

    // 2. Server path
    const serverPath = file.url || file.file_path || file.path;
    if (serverPath) {
      let fullUrl;
      if (serverPath.startsWith('http')) {
        fullUrl = serverPath;
      } else if (serverPath.startsWith('/')) {
        fullUrl = `${import.meta.env.VITE_API_URL || 'https://new.sbdemolition.online'}${serverPath}`;
      } else {
        fullUrl = `${import.meta.env.VITE_API_URL || 'https://new.sbdemolition.online'}/${serverPath}`;
      }
      return { url: fullUrl, blob: null, isServerFile: true };
    }

    // 3. IndexedDB
    if (file.storageType === 'indexedDB' || file.fileId) {
      try {
        const fileId = file.fileId || file.id;
        const fileRecord = await getFileFromIDB(fileId);
        if (fileRecord && fileRecord.file) {
          return { url: URL.createObjectURL(fileRecord.file), blob: fileRecord.file, isServerFile: false };
        }
      } catch (e) {
        console.error('IDB Retrieval Error:', e);
      }
    }

    // 4. Base64
    if (file.data) {
      return { url: file.data, blob: null, isServerFile: false };
    }

    // 5. Fallback
    if (file.id && file.id.startsWith('file_')) {
      const downloadUrl = `/api/company-files/download.php?id=${file.id}`;
      const fullUrl = `${import.meta.env.VITE_API_URL || 'https://new.sbdemolition.online'}${downloadUrl}`;
      return { url: fullUrl, blob: null, isServerFile: true };
    }

    addNotification('File Not Found', 'Could not locate file.', 'error');
    return null;
  };

  const handleViewFile = async (file) => {
    setLoadingFileId(file.id);
    try {
      const result = await getFileUrl(file);
      if (result) {
        const isViewable = file.name.match(/\.(pdf|jpg|jpeg|png|gif|webp|svg)$/i);

        if (isViewable) {
          openViewer(result.url, file.name);
        } else {
          if (result.isServerFile) {
            window.open(result.url, '_blank');
          } else {
            const link = document.createElement('a');
            link.href = result.url;
            link.download = file.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
          addNotification('Info', 'File type not supported for viewing, opened for download.', 'info');
        }
      }
    } catch (error) {
      addNotification('Error', error.message || 'Failed to view file', 'error');
    } finally {
      setLoadingFileId(null);
    }
  };

  const handleDownloadFile = async (file) => {
    setLoadingFileId(file.id);
    try {
      const result = await getFileUrl(file);
      if (result) {
        if (result.isServerFile) {
          window.open(result.url, '_blank');
          addNotification('Download Started', `Downloading ${file.name}`, 'success');
        } else {
          const link = document.createElement('a');
          link.href = result.url;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          if (result.blob) {
            setTimeout(() => URL.revokeObjectURL(result.url), 100);
          }
        }
      }
    } catch (error) {
      addNotification('Error', error.message || 'Failed to download file', 'error');
    } finally {
      setLoadingFileId(null);
    }
  };

  const downloadQuotationPdf = async (quotation) => {
    if (!quotation.fileId && !quotation.pdfFile) {
      addNotification('Download Error', 'This quotation has no attached PDF.', 'error');
      return;
    }

    try {
      if (quotation.pdfFile) {
        const link = document.createElement('a');
        link.href = quotation.pdfFile;
        link.download = quotation.pdfFileName || `Quotation-${quotation.quotationNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (quotation.fileId) {
        const fileRecord = await getFileFromIDB(quotation.fileId);
        if (fileRecord && fileRecord.file) {
          const url = URL.createObjectURL(fileRecord.file);
          const link = document.createElement('a');
          link.href = url;
          link.download = quotation.pdfFileName || `Quotation-${quotation.quotationNumber}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }
    } catch (e) {
      console.error('Download failed', e);
      addNotification('Download Error', 'Failed to download PDF', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h2 className="text-xl font-semibold mb-2">Project not found</h2>
        <Button onClick={() => navigate('/projects')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Button>
      </div>
    );
  }

  const getStatusBadge = () => {
    const statusConfig = {
      'New Inquiry': { color: 'bg-blue-100 text-blue-700 border-blue-200', label: 'New Inquiry' },
      'Site Visit Scheduled': { color: 'bg-purple-100 text-purple-700 border-purple-200', label: 'Site Visit Scheduled' },
      'Quotation Sent': { color: 'bg-yellow-100 text-yellow-700 border-yellow-200', label: 'Quotation Sent' },
      'Approved/Awarded': { color: 'bg-green-100 text-green-700 border-green-200', label: 'Approved/Awarded' },
      'Rejected/Lost': { color: 'bg-red-100 text-red-700 border-red-200', label: 'Rejected/Lost' }
    };

    const config = statusConfig[project.status] || statusConfig['New Inquiry'];
    return <Badge className={cn('border', config.color)}>{config.label}</Badge>;
  };

  return (
    <>
      <Helmet>
        <title>{project.projectName} | Project Details</title>
      </Helmet>

      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Header with Icon, Status and Edit */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/projects')}
              className="mt-1"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>

            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Briefcase className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">{project.projectName}</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {project.companyName} • {project.id}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {getStatusBadge()}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/projects/${id}/edit`)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </div>

        {/* Site Visit Bar */}
        <SiteVisitBar project={project} onUpdate={fetchProjectDetails} />

        {/* Quick Action Buttons */}
        <div className="grid grid-cols-3 gap-4">
          <Button
            variant="outline"
            className="h-auto py-4 flex-col gap-2"
            onClick={() => navigate(`/projects/${id}/accounting`)}
          >
            <DollarSign className="h-5 w-5" />
            <span>Accounting</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-4 flex-col gap-2"
            onClick={() => navigate(`/projects/${id}/quotation`)}
          >
            <FileText className="h-5 w-5" />
            <span>All Quotations</span>
          </Button>
          <Button
            variant="outline"
            className="h-auto py-4 flex-col gap-2"
            onClick={() => navigate(`/projects/${id}/lpo-contracts`)}
          >
            <Briefcase className="h-5 w-5" />
            <span>LPO & Contracts</span>
          </Button>
        </div>

        {/* Latest Quotation */}
        {latestQuotation && (
          <Card className="border-l-4 border-l-orange-500">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-orange-500 mt-0.5" />
                  <div>
                    <CardTitle className="text-lg">Latest Quotation</CardTitle>
                    <CardDescription>Most recent quotation generated for this project</CardDescription>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/projects/${id}/quotation`)}
                >
                  <Clock className="h-4 w-4 mr-2" />
                  View History
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <h3 className="text-xl font-semibold">{latestQuotation.quotationNumber}</h3>
                    <Badge variant="outline">{latestQuotation.version || 'R1'}</Badge>
                    <Badge>{latestQuotation.versionType || 'Original'}</Badge>
                    <span className="text-sm text-muted-foreground">
                      {latestQuotation.createdAt
                        ? new Date(latestQuotation.createdAt).toLocaleDateString()
                        : ''}
                    </span>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground uppercase mb-1">
                      Description
                    </p>
                    <p className="text-sm">{latestQuotation.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Unit Type</p>
                      <p className="text-sm">{latestQuotation.unitType || 'L/S'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Quantity</p>
                      <p className="text-sm">{latestQuotation.quantity || 1}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Unit Rate</p>
                      <p className="text-sm">
                        {formatCurrency(latestQuotation.price || 0)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end justify-center border-l pl-6">
                  <div className="text-right space-y-4 w-full">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total Amount</p>
                      <p className="text-3xl font-bold">
                        {formatCurrency(latestQuotation.total || 0)}
                      </p>
                    </div>

                    {latestQuotation.netValue !== undefined &&
                      latestQuotation.netValue !== latestQuotation.total && (
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Net Value</p>
                          <p className="text-2xl font-semibold text-green-600">
                            {formatCurrency(latestQuotation.netValue)}
                          </p>
                        </div>
                      )}

                    {(latestQuotation.fileId || latestQuotation.pdfFile) && (
                      <Button
                        variant="default"
                        size="sm"
                        className="w-full mt-4"
                        onClick={() => downloadQuotationPdf(latestQuotation)}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Project Info */}
            <Card>
              <CardHeader>
                <CardTitle>Project Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Work Type
                    </label>
                    <p className="mt-1">{project.workType || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Location
                    </label>
                    <p className="mt-1">{project.projectLocation || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Received By
                    </label>
                    <p className="mt-1">{project.receivedBy || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Created
                    </label>
                    <p className="mt-1">
                      {project.createdAt
                        ? new Date(project.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : 'N/A'}
                    </p>
                  </div>
                </div>

                {project.description && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Description
                    </label>
                    <p className="mt-1 text-sm">{project.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Email</label>
                  <p className="mt-1">{project.contactEmail || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Phone</label>
                  <p className="mt-1">{project.contactPhone || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Address</label>
                  <p className="mt-1">{project.companyAddress || 'N/A'}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Checklist */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                  <CardTitle>Project Checklist</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {project.checklist && project.checklist.length > 0 ? (
                  <div className="space-y-3">
                    {project.checklist.map(item => (
                      <div
                        key={item.id}
                        className="flex items-center gap-3 p-2 rounded hover:bg-muted"
                      >
                        <Checkbox
                          id={item.id}
                          checked={item.completed}
                          onCheckedChange={() => toggleChecklist(item.id)}
                        />
                        <label
                          htmlFor={item.id}
                          className={`flex-1 cursor-pointer text-sm ${
                            item.completed ? 'line-through text-muted-foreground' : ''
                          }`}
                        >
                          {item.label}
                        </label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    No checklist items defined.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* General Documents */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <File className="h-5 w-5 text-slate-600" />
                  <CardTitle>General Documents</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const generalDocs =
                    project.attachments?.filter(
                      f => !f.category || f.category === 'general'
                    ) || [];

                  return generalDocs.length > 0 ? (
                    <div className="space-y-2">
                      {generalDocs.map(file => (
                        <div
                          key={file.id}
                          className="flex items-center justify-between p-3 border rounded hover:bg-muted transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <File className="h-5 w-5 text-slate-500 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{file.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatBytes(file.size)}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewFile(file)}
                              disabled={loadingFileId === file.id}
                              title="View File"
                            >
                              {loadingFileId === file.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadFile(file)}
                              disabled={loadingFileId === file.id}
                              title="Download File"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      No general documents found.
                    </p>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Email Correspondence */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <CardTitle>Email Correspondence</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const emailDocs =
                    project.attachments?.filter(f => f.category === 'emails') || [];

                  return emailDocs.length > 0 ? (
                    <div className="space-y-2">
                      {emailDocs.map(file => (
                        <div
                          key={file.id}
                          className="flex items-center justify-between p-3 border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <Mail className="h-5 w-5 text-blue-500 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{file.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatBytes(file.size)}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewFile(file)}
                              disabled={loadingFileId === file.id}
                              title="View Email"
                            >
                              {loadingFileId === file.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadFile(file)}
                              disabled={loadingFileId === file.id}
                              title="Download Email"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      No email correspondence found.
                    </p>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Timeline */}
        {project.timeline && project.timeline.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <CardTitle>Timeline</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {project.timeline.map((event, index) => {
                  const eventDate = new Date(event.date);
                  const dateStr = eventDate.toLocaleDateString('en-US', {
                    month: 'numeric',
                    day: 'numeric',
                    year: 'numeric'
                  });
                  const timeStr = eventDate.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  });

                  return (
                    <div key={event.id || index} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="h-2 w-2 rounded-full bg-orange-500" />
                        {index < project.timeline.length - 1 && (
                          <div className="h-full w-px bg-border mt-1" />
                        )}
                      </div>
                      <div className="flex-1 pb-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm text-muted-foreground">
                                {dateStr} • {timeStr}
                              </p>
                            </div>
                            <p className="font-medium">{event.title}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {event.description}
                              {event.user && event.user !== 'System' && (
                                <span className="ml-1">— by {event.user}</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};

export default ProjectDetailsPage;
